<template>
  <div
    @click="onContractClick"
    :class="classModifiers"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <div class="market-contract-horizontal-sushi__header">
      <div class="market-contract-horizontal-sushi__image-title">
        <div class="market-contract-horizontal-sushi__image" :style="contractImageStyleModifiers">
          <div v-if="showSharesBar" :class="sharesOwnedStyleModifiers"></div>
        </div>
        <div class="market-contract-horizontal-sushi__title">
          <div class="market-contract-horizontal-sushi__title-text">
            {{ title }}
          </div>
        </div>
      </div>

      <div class="market-contract-horizontal-sushi__ownership-buttons">
        <!-- Dashboard: desktop layout with equal-gap columns wrapper -->
        <div
          v-if="dashboardMode && useDesktopLayout"
          class="market-contract-horizontal-sushi__middle-columns"
        >
          <template v-if="!forCmsWidget">
            <div class="market-contract-horizontal-sushi__type">
              {{ contractModel.mode === 'no' ? 'NO' : 'YES' }}
            </div>
          </template>
          <div class="market-contract-horizontal-sushi__last-trade">
            <div class="market-contract-horizontal-sushi__last-trade-price">
              <template v-if="currentPriceDashboard > 0">
                <div class="market-contract-horizontal-sushi__last-trade-price-value-col">
                  <animated-number
                    class="market-contract-horizontal-sushi__last-trade-price-value"
                    :value="currentPriceDashboard"
                    :symbol="currentPriceSymbol"
                  ></animated-number>
                </div>
                <div class="market-contract-horizontal-sushi__last-trade-price-change-col">
                  <market-change-price
                    v-if="showChangePriceIndicator && useDesktopLayout"
                    :price="
                      contractModel.mode === 'no'
                        ? -contractModel.changePriceAsCents
                        : contractModel.changePriceAsCents
                    "
                    :lastClosePrice="contractModel.lastClosePrice"
                    :symbol="currentPriceSymbol"
                  ></market-change-price>
                </div>
              </template>
              <span class="market-contract-horizontal-sushi__last-trade-price-new" v-else>NEW</span>
            </div>
          </div>
          <template v-if="!forCmsWidget">
            <div class="market-contract-horizontal-sushi__volume">
              <div class="market-contract-horizontal-sushi__volume-value">
                <div v-if="contract.userQuantity > 0">{{ volumeDisplay }}</div>
                <div v-else class="market-dash-symbol">-</div>
              </div>
            </div>
            <div class="market-contract-horizontal-sushi__avg-paid">
              <div v-if="contract.userQuantity === 0" class="market-dash-symbol">-</div>
              <template v-else>
                <div v-price-display.cents="contractModel.userAveragePricePerShareAsCents"></div>
                <market-change-price
                  v-if="showChangePriceIndicatorUserChange"
                  :price="contractModel.userChangePriceAsCents"
                  :lastClosePrice="contractModel.lastClosePrice"
                />
              </template>
            </div>
            <div
              class="market-contract-horizontal-sushi__buy-orders market-contract-horizontal-sushi__tooltip"
              :data-tooltip="buyOrdersTooltip"
            >
              <div v-if="contractModel.userOpenOrdersBuyQuantity > 0">{{ buyOrdersDisplay }}</div>
              <div v-else class="market-dash-symbol">-</div>
            </div>
            <div
              class="market-contract-horizontal-sushi__sell-orders market-contract-horizontal-sushi__tooltip"
              :data-tooltip="sellOrdersTooltip"
            >
              <div v-if="contractModel.userOpenOrdersSellQuantity > 0">{{ sellOrdersDisplay }}</div>
              <div v-else class="market-dash-symbol">-</div>
            </div>
          </template>
        </div>

        <!-- Non-dashboard: original layout for desktop details page + mobile -->
        <template v-else>
          <div class="market-contract-horizontal-sushi__last-trade">
            <div class="market-contract-horizontal-sushi__last-trade-price">
              <template v-if="currentPrice > 0">
                <market-change-price
                  v-if="contractModel.changePriceAsCents && isLayoutMobile"
                  :price="contractModel.changePriceAsCents"
                  :lastClosePrice="contractModel.lastClosePrice"
                  :symbol="currentPriceSymbol"
                ></market-change-price>
                <div class="market-contract-horizontal-sushi__last-trade-price-value-col">
                  <animated-number
                    class="market-contract-horizontal-sushi__last-trade-price-value"
                    :value="currentPrice"
                    :symbol="currentPriceSymbol"
                  ></animated-number>
                </div>
                <div class="market-contract-horizontal-sushi__last-trade-price-change-col">
                  <market-change-price
                    v-if="contractModel.changePriceAsCents && useDesktopLayout"
                    :price="contractModel.changePriceAsCents"
                    :lastClosePrice="contractModel.lastClosePrice"
                    :symbol="currentPriceSymbol"
                  ></market-change-price>
                </div>
              </template>
              <template v-else>
                <div class="market-contract-horizontal-sushi__last-trade-price-value-col">
                  <span class="market-contract-horizontal-sushi__last-trade-price-new">NEW</span>
                </div>
                <div class="market-contract-horizontal-sushi__last-trade-price-change-col">
                  &nbsp;
                </div>
              </template>
            </div>
          </div>
          <div v-if="showSharesBar" class="market-contract-horizontal-sushi__ownership">
            <market-shares-ownership
              @click="onClickSharesBar"
              :mode="contractModel.mode"
              :number="contract.userQuantity"
            />
          </div>
          <div
            v-else-if="useDesktopLayout && !forCmsWidget"
            class="market-contract-horizontal-sushi__volume"
          >
            <div class="market-contract-horizontal-sushi__volume-value">
              <span class="market-dash-symbol">-</span>
            </div>
          </div>
          <div
            v-else-if="useMobileLayout && isTradingEnabled"
            class="market-contract-horizontal-sushi__empty-space"
          ></div>
          <div
            v-if="useDesktopLayout && !forCmsWidget"
            class="market-contract-horizontal-sushi__avg-paid"
          >
            <span v-if="contract.userQuantity === 0" class="market-dash-symbol">-</span>
            <template v-else>
              <span v-price-display.cents="contractModel.userAveragePricePerShareAsCents"></span>
              <market-change-price
                v-if="showChangePriceIndicatorUserChange"
                :price="contractModel.userChangePriceAsCents"
                :lastClosePrice="contractModel.lastClosePrice"
                :symbol="currentPriceSymbol"
              />
            </template>
          </div>
          <div
            v-if="useDesktopLayout && !forCmsWidget"
            class="market-contract-horizontal-sushi__orders"
          >
            <market-contract-horizontal-sushi-orders
              :buyQuantity="contractModel.userOpenOrdersBuyQuantity"
              :sellQuantity="contractModel.userOpenOrdersSellQuantity"
              :mode="contractModel.mode"
            />
          </div>
        </template>

        <div class="market-contract-horizontal-sushi__buttons">
          <template v-if="isMarketDisabled && !forCmsWidget">
            <market-disabled-outcome-v2
              :market="market"
              :contract="contract"
            ></market-disabled-outcome-v2>
          </template>
          <template v-else>
            <!-- Left buttons -->
            <div class="market-contract-horizontal-sushi__buttons--left">
              <!-- CMS widget: always YES, ignore ownership mode -->
              <market-button-direction
                v-if="forCmsWidget"
                variant="buy-yes"
                :disabled="cmsTradeDisabled"
                :title="cmsTradeDisabled ? 'Trading is currently unavailable' : null"
                :price="contractModel.buyYesCostAsCents"
                text="YES"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.BUY_YES.type,
                    BUTTONS.BUY_YES.value,
                    BUTTONS.BUY_YES.id,
                  )
                "
              />
              <!-- User has NO shares: Buy NO -->
              <market-button-direction
                v-else-if="contractModel.mode === VALUES.NO"
                variant="buy-no"
                :price="contractModel.buyNoCostAsCents"
                text="NO"
                :isActive="tradeOptions.contractId === contract.contractId"
                :isOn="activeButton === BUTTONS.BUY_NO.id"
                :operationValue="tradeOptions.operationValue"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.BUY_NO.type,
                    BUTTONS.BUY_NO.value,
                    BUTTONS.BUY_NO.id,
                  )
                "
              />
              <!-- User has YES shares or no shares: Buy YES -->
              <market-button-direction
                v-else
                variant="buy-yes"
                :price="contractModel.buyYesCostAsCents"
                text="YES"
                :isActive="tradeOptions.contractId === contract.contractId"
                :isOn="activeButton === BUTTONS.BUY_YES.id"
                :operationValue="tradeOptions.operationValue"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.BUY_YES.type,
                    BUTTONS.BUY_YES.value,
                    BUTTONS.BUY_YES.id,
                  )
                "
              />
            </div>

            <!-- Right buttons -->
            <div class="market-contract-horizontal-sushi__buttons--right">
              <!-- CMS widget: always NO buy, ignore ownership mode -->
              <market-button-direction
                v-if="forCmsWidget"
                variant="buy-no"
                :disabled="cmsTradeDisabled"
                :title="cmsTradeDisabled ? 'Trading is currently unavailable' : null"
                :price="contractModel.buyNoCostAsCents"
                text="NO"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.BUY_NO.type,
                    BUTTONS.BUY_NO.value,
                    BUTTONS.BUY_NO.id,
                  )
                "
              />
              <!-- User has NO shares: Sell NO -->
              <market-button-direction
                v-else-if="contractModel.mode === VALUES.NO"
                variant="sell-no"
                :price="contractModel.sellNoCostAsCents"
                text="Sell"
                :isActive="tradeOptions.contractId === contract.contractId"
                :isOn="activeButton === BUTTONS.SELL_NO.id"
                :operationValue="tradeOptions.operationValue"
                :saleDisabled="contractModel.userQuantity === 0"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.SELL_NO.type,
                    BUTTONS.SELL_NO.value,
                    BUTTONS.SELL_NO.id,
                  )
                "
              />
              <!-- User has YES shares: Sell YES -->
              <market-button-direction
                v-else-if="contractModel.mode === VALUES.YES"
                variant="sell-yes"
                :price="contractModel.sellYesCostAsCents"
                text="Sell"
                :isActive="tradeOptions.contractId === contract.contractId"
                :isOn="activeButton === BUTTONS.SELL_YES.id"
                :operationValue="tradeOptions.operationValue"
                :saleDisabled="contractModel.userQuantity === 0"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.SELL_YES.type,
                    BUTTONS.SELL_YES.value,
                    BUTTONS.SELL_YES.id,
                  )
                "
              />
              <!-- No shares: Buy NO -->
              <market-button-direction
                v-else
                variant="buy-no"
                :price="contractModel.buyNoCostAsCents"
                text="NO"
                :isActive="tradeOptions.contractId === contract.contractId"
                :isOn="activeButton === BUTTONS.BUY_NO.id"
                :operationValue="tradeOptions.operationValue"
                @click="
                  openPurchaseJourney(
                    contractModel.contractId,
                    BUTTONS.BUY_NO.type,
                    BUTTONS.BUY_NO.value,
                    BUTTONS.BUY_NO.id,
                  )
                "
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="./market-contract-horizontal-sushi.js"></script>
<style src="./market-contract-horizontal-sushi.scss" lang="scss"></style>
